import React, { useEffect, useState } from 'react'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper'
import { Box, Card, CardMedia, Typography } from '@mui/material'

import { makeStyles } from 'tss-react/mui'
import { useShop } from 'src/Libs/shop/hooks'
import { ProductResponse } from '../../models'
import { ShopMainSkeleton } from 'src/Libs/shop/components/Skeletons'
import MoreInfoProductCategories from './MoreInfoProductCategories'
import { currencyFormatter } from '../../utils/formatter'
import { ProductType } from '../../types'
import { getImageUrlBySize } from '../../utils'
import { ShopImageThumnbailSizes } from '../../types'
import { QuantityAvailabilityIndicator } from '../ShopProduct'

export interface MoreInfoModalProps {
  productId: string
  open: boolean
  onClose: () => void
  place: number
}

const useStyles = makeStyles()(theme => ({
  dialog: {
    '& .MuiDialogContent-root': {
      scrollbarGutter: 'stable',
    },
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '--swiper-theme-color': theme.palette.grey.grey09,
    '.swiper-pagination-bullet-active': {
      transform: 'scale(1.5)',
    },
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
  },
}))

/*
 * This component shows detailed data for a single Product like the Product Component, but instead of a page this is a popup.
 */
const MoreInfoModal: React.FC<MoreInfoModalProps> = ({ open, place, onClose, productId }): JSX.Element => {
  const [productData, setProductData] = useState<ProductResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { t } = useCustomTranslation()
  const { getShopProduct } = useShop()

  const { classes } = useStyles()

  useEffect(() => {
    setIsLoading(true)
    getShopProduct(productId)
      .then(result => {
        setProductData(result)
      })
      .finally(() => {
        setIsLoading(false)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  const discount = productData && productData.product?.calculatedPrice?.unitPrice < (productData.product?.calculatedPrice?.listPrice?.price ?? 0)
  const unitPrice = productData?.product?.calculatedPrice?.unitPrice
  const hasUnitPrice = productData?.product?.calculatedPrice?.unitPrice ?? false
  const hasListPrice = productData?.product?.calculatedPrice?.listPrice?.price
  const isProductSystemBase = productData?.product.extensions.e3dcData.productType === ProductType.SystemBase
  const additionalProductNumber = productData?.product.extensions.e3dcData?.additionalProductNumber
  const normalProductNumber = productData?.product.productNumber
  const productNumber = isProductSystemBase && additionalProductNumber ? additionalProductNumber : normalProductNumber
  const productAvailability = productData?.product.extensions?.e3dcAvailability
  const productImages = productData?.product.media
  return (
    <Dialog className={classes.dialog} divider={false} maxWidth="sm" onClose={onClose} place={place}>
      {isLoading ? (
        <ShopMainSkeleton shopSkeletonType="product" />
      ) : (
        <>
          {productData && (
            <>
              <Box>
                {productData.product.manufacturer?.translated && (
                  <Typography variant="h5" color="primary">
                    {productData.product.manufacturer.translated.name}
                  </Typography>
                )}
                {productData.product?.translated && (
                  <Typography variant="h1">
                    {productData.product.translated.name} <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{`${t('shop.product.number')} ${productNumber}`}</Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Typography variant="h6" color={discount ? 'primary' : 'inherit'}>
                      {hasUnitPrice ? currencyFormatter(unitPrice) : currencyFormatter(hasListPrice)}
                    </Typography>
                    {discount && (
                      <Typography variant="h6" sx={{ textDecorationLine: 'line-through', marginLeft: '32px' }}>
                        {currencyFormatter(hasListPrice)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Swiper
                  pagination={{
                    clickable: true,
                  }}
                  loop
                  navigation={true}
                  spaceBetween={30}
                  modules={[Navigation, Pagination]}
                  className={classes.imageContainer}
                >
                  {productImages
                    ?.sort((a, b) => a.position - b.position)
                    .map(image => {
                      const productImageUrl = getImageUrlBySize(image?.media?.thumbnails, ShopImageThumnbailSizes.Large)
                      return (
                        <SwiperSlide key={image.position} className={classes.image}>
                          <Card sx={{ my: 1, padding: 0 }}>
                            <CardMedia className={classes.image} component="img" height="194" image={productImageUrl} alt="System konfigurieren" />
                          </Card>
                        </SwiperSlide>
                      )
                    })}
                </Swiper>
              </Box>
              <MoreInfoProductCategories
                productDescription={productData.product.translated?.description}
                width={productData.product.width}
                height={productData.product.height}
                length={productData.product.length}
                weight={productData.product.weight}
                properties={productData.product.properties}
                documents={productData.product.extensions.downloads.downloads}
              />
            </>
          )}
        </>
      )}
    </Dialog>
  )
}

export default MoreInfoModal
